// One box over time (`/admin/system/hosts/:hostId`): NextAdmin demo `/analytics` — a period control, then a grid of
// chart cards. Each chart is one server-side panel (`metric-panels.ts`), every point the average (and peak) of its step.
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { DashboardGrid } from '../../../components/page/DashboardPage.js';
import { DetailPage } from '../../../components/page/DetailPage.js';
import { Badge, Callout, Card, DataTable, type DataTableColumn } from '../../../components/ui/index.js';
import { getSystemHostOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { SystemHost } from '../../../generated/api/types.gen.js';
import { PAGE_HELP } from '../../../help/index.js';
import { ApiError } from '../../../services/api/errors.js';
import { roleLabel } from './HostCard.js';
import { HostMetricChart, type HostPanel } from './HostMetricChart.js';
import { formatAgo, formatBytes, formatRatio, formatUptime } from './metric-format.js';
import { RangePills, useRangeParam } from './RangePills.js';

type Filesystem = SystemHost['filesystems'][number];

const FILESYSTEM_COLUMNS: DataTableColumn<Filesystem>[] = [
  { key: 'mountpoint', header: 'Mounted at', render: (f) => <span className="font-mono text-xs">{f.mountpoint}</span> },
  { key: 'size', header: 'Size', align: 'right', render: (f) => formatBytes(f.sizeBytes) },
  { key: 'avail', header: 'Free', align: 'right', render: (f) => formatBytes(f.availBytes) },
  { key: 'used', header: 'Used', align: 'right', render: (f) => formatRatio(f.usedRatio) },
];

interface ChartSpec {
  panel: HostPanel;
  title: string;
  description: string;
  area?: boolean;
  gpu?: boolean;
}

const CHARTS: readonly ChartSpec[] = [
  { panel: 'cpu', title: 'CPU', description: 'Share of all cores busy, waiting on disk, and the busiest single core' },
  { panel: 'load', title: 'Load', description: 'Runnable work per CPU: above 100 % means work is queueing' },
  { panel: 'memory', title: 'Memory', description: 'Used and total memory, and swap in use', area: true },
  {
    panel: 'pressure',
    title: 'Pressure',
    description: 'Share of time work waited for CPU, memory or disk: the bottleneck signal',
  },
  { panel: 'disk-busy', title: 'Disk busy', description: 'Share of time each disk was serving requests' },
  { panel: 'disk-io', title: 'Disk throughput', description: 'Bytes read and written per second, per disk' },
  { panel: 'filesystems', title: 'Disk space', description: 'How full each filesystem is' },
  { panel: 'network', title: 'Network', description: 'Bytes received and sent per second, per interface' },
  { panel: 'units-cpu', title: 'CPU by container and service', description: 'The 8 busiest over the range, in cores' },
  {
    panel: 'units-memory',
    title: 'Memory by container and service',
    description: 'The 8 largest over the range',
    area: true,
  },
  { panel: 'temperature', title: 'Temperature', description: 'The hottest sensor' },
  { panel: 'gpu', title: 'GPU', description: 'Share of time the GPU was busy', gpu: true },
  { panel: 'gpu-temperature', title: 'GPU temperature', description: 'Per GPU', gpu: true },
  { panel: 'gpu-power', title: 'GPU power', description: 'Per GPU', gpu: true },
];

export function SystemHostPage() {
  const { hostId = '' } = useParams<{ hostId: string }>();
  const [range, setRange] = useRangeParam();
  const query = useQuery({ ...getSystemHostOptions({ path: { id: hostId } }), refetchInterval: 30_000 });
  const host = query.data;
  const nowMs = Date.now();
  const notFound = query.error instanceof ApiError && query.error.status === 404;

  return (
    <DetailPage
      title={hostId}
      subtitle="How hard this box works now and over time"
      breadcrumbs={[{ label: 'System', to: '/admin/system' }, { label: hostId }]}
      helpContent={PAGE_HELP['system-host']}
      badges={
        host && (
          <>
            <Badge variant={host.online ? 'success' : 'danger'} dot>
              {host.online ? 'Online' : 'Offline'}
            </Badge>
            <Badge variant="neutral">{roleLabel(host.role)}</Badge>
          </>
        )
      }
      actions={<RangePills range={range} onChange={setRange} />}
      meta={
        host && (
          <span>
            {[
              host.kernel && `Linux ${host.kernel}${host.architecture ? ` (${host.architecture})` : ''}`,
              host.cpuCount !== null && `${host.cpuCount} ${host.cpuCount === 1 ? 'CPU' : 'CPUs'}`,
              host.memoryTotalBytes !== null && `${formatBytes(host.memoryTotalBytes)} RAM`,
              host.online ? `up ${formatUptime(host.uptimeSeconds)}` : `last seen ${formatAgo(host.lastSeenAt, nowMs)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )
      }
      alert={
        query.isError ? (
          <Callout tone="danger" icon={AlertTriangle} title={notFound ? 'No such box' : 'This box could not be loaded'}>
            {notFound
              ? `No box called "${hostId}" has reported to the metrics hub.`
              : query.error instanceof ApiError
                ? query.error.message
                : 'The request failed.'}
          </Callout>
        ) : undefined
      }
    >
      {!notFound && (
        <DashboardGrid>
          {CHARTS.filter((chart) => !chart.gpu || host?.hasGpu).map((chart) => (
            <div key={chart.panel} className="xl:col-span-6">
              <HostMetricChart
                hostId={hostId}
                panel={chart.panel}
                range={range}
                title={chart.title}
                description={chart.description}
                area={chart.area}
              />
            </div>
          ))}
          {host && host.filesystems.length > 0 && (
            <Card title="Filesystems now" className="xl:col-span-12">
              <DataTable
                columns={FILESYSTEM_COLUMNS}
                rows={host.filesystems}
                rowKey={(f) => f.mountpoint}
                getRowAttention={(f) =>
                  (f.usedRatio ?? 0) > 0.92 ? 'danger' : (f.usedRatio ?? 0) > 0.85 ? 'warning' : undefined
                }
              />
            </Card>
          )}
        </DashboardGrid>
      )}
    </DetailPage>
  );
}
